import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AlertCircle, Eye, EyeOff, Lock, Mail } from 'lucide-react';

import {
  FieldError,
  FieldGroup,
  Form,
  Input,
  InputWrapper,
  Label,
  ServerError,
  Spinner,
  SubmitButton,
  ToggleVisibility,
} from './login-form.styles';

const schema = Yup.object({
  email: Yup.string()
    .trim()
    .email('Informe um e-mail válido')
    .required('E-mail é obrigatório'),
  password: Yup.string()
    .min(6, 'A senha deve ter ao menos 6 caracteres')
    .required('Senha é obrigatória'),
});

export interface LoginFormValues {
  email: string;
  password: string;
}

interface LoginFormProps {
  onSubmit: (values: LoginFormValues) => Promise<void> | void;
  serverError?: string | null;
  isLoading?: boolean;
  onDismissError?: () => void;
}

export const LoginForm = ({
  onSubmit,
  serverError,
  isLoading,
  onDismissError,
}: LoginFormProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik<LoginFormValues>({
    initialValues: { email: '', password: '' },
    validationSchema: schema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values) => {
      await onSubmit({
        email: values.email.trim(),
        password: values.password,
      });
    },
  });

  const emailError = formik.touched.email && formik.errors.email;
  const passwordError = formik.touched.password && formik.errors.password;

  return (
    <Form
      noValidate
      onSubmit={formik.handleSubmit}
      aria-describedby={serverError ? 'login-server-error' : undefined}
    >
      <FieldGroup>
        <Label htmlFor="email">E-mail</Label>
        <InputWrapper $hasError={Boolean(emailError)}>
          <Mail size={18} aria-hidden="true" />
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="voce@vendepay.com.br"
            value={formik.values.email}
            onChange={(e) => {
              formik.handleChange(e);
              if (serverError) {
                onDismissError?.();
              }
            }}
            onBlur={formik.handleBlur}
            disabled={isLoading}
            aria-invalid={Boolean(emailError)}
            aria-describedby={emailError ? 'email-error' : undefined}
          />
        </InputWrapper>
        {emailError && (
          <FieldError id="email-error" role="alert">
            <AlertCircle size={13} aria-hidden="true" />
            {formik.errors.email}
          </FieldError>
        )}
      </FieldGroup>

      <FieldGroup>
        <Label htmlFor="password">Senha</Label>
        <InputWrapper $hasError={Boolean(passwordError)}>
          <Lock size={18} aria-hidden="true" />
          <Input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="Sua senha"
            value={formik.values.password}
            onChange={(e) => {
              formik.handleChange(e);
              if (serverError) {
                onDismissError?.();
              }
            }}
            onBlur={formik.handleBlur}
            disabled={isLoading}
            aria-invalid={Boolean(passwordError)}
            aria-describedby={passwordError ? 'password-error' : undefined}
          />
          <ToggleVisibility
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            aria-pressed={showPassword}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </ToggleVisibility>
        </InputWrapper>
        {passwordError && (
          <FieldError id="password-error" role="alert">
            <AlertCircle size={13} aria-hidden="true" />
            {formik.errors.password}
          </FieldError>
        )}
      </FieldGroup>

      {serverError && (
        <ServerError id="login-server-error" role="alert" $shake>
          <AlertCircle size={16} aria-hidden="true" />
          <span>{serverError}</span>
        </ServerError>
      )}

      <SubmitButton type="submit" disabled={isLoading} aria-busy={isLoading}>
        {isLoading ? (
          <>
            <Spinner aria-hidden="true" /> Entrando…
          </>
        ) : (
          'Entrar'
        )}
      </SubmitButton>
    </Form>
  );
};
